/**
 * analyzeDebugLogs.ts
 * @deprecated Ce script n'est actuellement pas utilisé dans l'application (imported_by_total = 0).
 * Il est conservé pour référence future mais peut être supprimé si non nécessaire.
 *
 * Script d'analyse des logs de débogage
 * À exécuter dans la console du navigateur pour analyser les logs
 */

import { getStoredLogs, exportLogs } from './debugLogger';

export interface LogAnalysis {
  totalLogs: number;
  byHypothesis: Record<string, number>;
  byLocation: Record<string, number>;
  coordinatorNullCount: number;
  coordinatorAvailableCount: number;
  errors: Array<{ location: string; message: string; data: unknown }>;
  timeline: Array<{ timestamp: number; location: string; message: string; hypothesisId: string }>;
}

/**
 * Analyse les logs stockés et génère un rapport
 */
export function analyzeLogs(): LogAnalysis {
  const logs = getStoredLogs();

  const analysis: LogAnalysis = {
    totalLogs: logs.length,
    byHypothesis: {},
    byLocation: {},
    coordinatorNullCount: 0,
    coordinatorAvailableCount: 0,
    errors: [],
    timeline: [],
  };

  for (const log of logs) {
    // Compter par hypothèse
    analysis.byHypothesis[log.hypothesisId] = (analysis.byHypothesis[log.hypothesisId] || 0) + 1;

    // Compter par location
    analysis.byLocation[log.location] = (analysis.byLocation[log.location] || 0) + 1;

    // Analyser les données pour AppCoordinator
    if (log.data && typeof log.data === 'object') {
      const data = log.data as Record<string, unknown>;
      if ('coordinatorAvailable' in data) {
        if (data.coordinatorAvailable === true) {
          analysis.coordinatorAvailableCount++;
        } else if (data.coordinatorAvailable === false) {
          analysis.coordinatorNullCount++;
        }
      }
      if ('appCoordinatorAvailable' in data) {
        if (data.appCoordinatorAvailable === true) {
          analysis.coordinatorAvailableCount++;
        } else if (data.appCoordinatorAvailable === false) {
          analysis.coordinatorNullCount++;
        }
      }
      if ('hasCoordinator' in data) {
        if (data.hasCoordinator === true) {
          analysis.coordinatorAvailableCount++;
        } else if (data.hasCoordinator === false) {
          analysis.coordinatorNullCount++;
        }
      }
      if ('returningCoordinator' in data) {
        if (data.returningCoordinator === true) {
          analysis.coordinatorAvailableCount++;
        } else if (data.returningCoordinator === false) {
          analysis.coordinatorNullCount++;
        }
      }
      if ('finalCheckAvailable' in data) {
        if (data.finalCheckAvailable === true) {
          analysis.coordinatorAvailableCount++;
        } else if (data.finalCheckAvailable === false) {
          analysis.coordinatorNullCount++;
        }
      }
    }

    // Détecter les erreurs
    if (
      log.message.toLowerCase().includes('error') ||
      log.message.toLowerCase().includes('failed')
    ) {
      analysis.errors.push({
        location: log.location,
        message: log.message,
        data: log.data,
      });
    }

    // Ajouter à la timeline
    analysis.timeline.push({
      timestamp: log.timestamp,
      location: log.location,
      message: log.message,
      hypothesisId: log.hypothesisId,
    });
  }

  // Trier la timeline par timestamp
  analysis.timeline.sort((a, b) => a.timestamp - b.timestamp);

  return analysis;
}

/**
 * Affiche un rapport d'analyse dans la console
 */
// P3 : retourne LogAnalysis (utile pour exposer dans window.analyzeDebugLogs).
export function printAnalysis(): LogAnalysis {
  const analysis = analyzeLogs();

  console.group('📊 Analyse des Logs de Débogage');
  console.log(`Total de logs: ${analysis.totalLogs}`);
  console.log('');

  console.group('Par Hypothèse:');
  for (const [hypothesisId, count] of Object.entries(analysis.byHypothesis)) {
    console.log(`  ${hypothesisId}: ${count} logs`);
  }
  console.groupEnd();
  console.log('');

  console.group('AppCoordinator Status:');
  console.log(`  Disponible: ${analysis.coordinatorAvailableCount} fois`);
  console.log(`  Null: ${analysis.coordinatorNullCount} fois`);
  console.groupEnd();
  console.log('');

  if (analysis.errors.length > 0) {
    console.group('❌ Erreurs détectées:');
    for (const error of analysis.errors) {
      console.error(`  ${error.location}: ${error.message}`, error.data);
    }
    console.groupEnd();
    console.log('');
  }

  console.group('Timeline (premiers 20 événements):');
  for (const event of analysis.timeline.slice(0, 20)) {
    const date = new Date(event.timestamp);
    console.log(
      `  [${date.toLocaleTimeString()}] ${event.hypothesisId} - ${event.location}: ${event.message}`
    );
  }
  console.groupEnd();

  console.groupEnd();

  return analysis;
}

// Exposer globalement pour utilisation dans la console
if (typeof window !== 'undefined') {
  (window as unknown as { analyzeDebugLogs: typeof printAnalysis }).analyzeDebugLogs =
    printAnalysis;
  (window as unknown as { exportDebugLogs: typeof exportLogs }).exportDebugLogs = exportLogs;
}
